import styled from 'styled-components';
import Button from '../../components/Button';
import { PHONE_MEDIA_QUERY } from '../../common/responsive';
import PartnersScroll from './Scroll';
import Answers from '../../components/Answers';
import ScrollRestore from '../../components/ScrollRestore';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  padding-top: 40px;
  padding-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 600;
`;

const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 400;
`;

const PricingContainer = styled.div`
  width: 365px;
  div {
    z-index: 2;
  }
  min-height: 480px;
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.border.secondary};
  padding: 24px;
  display: flex;
  flex-direction: column;
  height: -webkit-fill-available;
  position: relative;
`;

const PricingRow = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  justify-content: center;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
  }
`;

const PricingTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 500;
`;

const PricingTitleAndPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const PricingPrice = styled.h3`
  font-size: 2.5rem;
  font-weight: 600;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.secondary};
`;

const IncludesLabel = styled.h3`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.text.lighter};
  font-weight: 500;
`;

const InfoContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  width: 100%;
  gap: 16px;
`;

const Feature = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  width: 100%;
  gap: 8px;
`;

const FeatureIcon = styled.img`
  width: 10px;
  height: 10px;
`;

const FeatureText = styled.h3`
  font-size: 1rem;
  font-weight: 500;
`;

const ButtonContainer = styled.div``;

const ProfessionalImg = styled.img`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 0;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
`;

const Spacer = styled.div`
  flex: 1;
  min-height: 40px;
`;

const TruestedByContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 120px;
  gap: 60px;
`;

const TruestedByText = styled.h3`
  font-size: 1.2rem;
  font-weight: 500;
`;

const PricingDetails = styled.span`
  font-size: 0.9rem;
  font-weight: 400;
  color: ${({ theme }) => theme.text.lighter};
`;

const BackedByAlliance = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 12px;
  margin-top: 40px;
`;

const AllianceLogo = styled.img`
  width: 100px;
`;

const Pricing = () => {
  return (
    <Container>
      <ScrollRestore />
      <Title>Pricing</Title>
      <Subtitle>
        We offer a range of pricing options to suit your needs.
      </Subtitle>
      <PricingRow>
        <PricingContainer>
          <InfoContainer>
            <PricingTitleAndPriceContainer>
              <PricingTitle>Starter</PricingTitle>
              <PricingPrice>Free</PricingPrice>
            </PricingTitleAndPriceContainer>
            <Line />
            <IncludesLabel>Includes</IncludesLabel>
            <FeaturesContainer>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Digital gift cards</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>1 Free Design</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Token trading</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Send Gift Cards via Link</FeatureText>
              </Feature>
            </FeaturesContainer>
          </InfoContainer>
          <Spacer />
          <ButtonContainer>
            <Button
              variant='secondary'
              as='a'
              href='https://calendly.com/kevin-deks/deks-platform-intro'
              target='_blank'
              rel='noopener noreferrer'
            >
              GET STARTED
            </Button>
          </ButtonContainer>
        </PricingContainer>
        <PricingContainer>
          <ProfessionalImg src={'/pricing/professional.webp'} />
          <InfoContainer>
            <PricingTitleAndPriceContainer>
              <PricingTitle>Professional</PricingTitle>
              <PricingPrice>
                $199<PricingDetails> /One Time Fee</PricingDetails>
              </PricingPrice>
            </PricingTitleAndPriceContainer>
            <Line />
            <IncludesLabel>Everything in starter, plus:</IncludesLabel>
            <FeaturesContainer>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Physical gift card functionality</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>3 Gift Card Designs</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Customizable token page</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Boosted listing</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Metrics dashboard (upcoming)</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Vested tokens gift cards (upcoming)</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Perp Trading Functionality (Optional)</FeatureText>
              </Feature>
            </FeaturesContainer>
          </InfoContainer>
          <Spacer />
          <ButtonContainer>
            <Button
              variant='secondary'
              as='a'
              href='https://calendly.com/kevin-deks/deks-platform-intro'
              target='_blank'
              rel='noopener noreferrer'
            >
              GET PROFESSIONAL
            </Button>
          </ButtonContainer>
        </PricingContainer>
        <PricingContainer>
          <InfoContainer>
            <PricingTitleAndPriceContainer>
              <PricingTitle>Enterprise</PricingTitle>
              <PricingPrice>
                $700<PricingDetails> /One Time Fee</PricingDetails>
              </PricingPrice>
            </PricingTitleAndPriceContainer>
            <Line />
            <IncludesLabel>Everything in professional, plus:</IncludesLabel>
            <FeaturesContainer>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Push notifications</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>6 Gift Card Designs</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Revenue share</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon src={'check.webp'} />
                <FeatureText>Enhanced Co-Marketing Campaign</FeatureText>
              </Feature>
            </FeaturesContainer>
          </InfoContainer>
          <Spacer />
          <ButtonContainer>
            <Button
              variant='secondary'
              as='a'
              href='https://calendly.com/kevin-deks/deks-platform-intro'
              target='_blank'
              rel='noopener noreferrer'
            >
              GET ENTERPRISE
            </Button>
          </ButtonContainer>
        </PricingContainer>
      </PricingRow>
      <BackedByAlliance>
        <p>Backed by</p>
        <AllianceLogo src={'/pricing/alliance.webp'} />
      </BackedByAlliance>
      <TruestedByContainer>
        <TruestedByText>Trusted by</TruestedByText>
        <PartnersScroll />
      </TruestedByContainer>
      <div
        style={{
          width: '100%',
        }}
      >
        <Answers
          questions={[
            {
              question: 'What are digital gift cards?',
              answer:
                'Digital gift cards let you send crypto instantly to anyone. Perfect for giveaways, events, rewards, or onboarding new users—no wallet setup required.',
            },
            {
              question: 'Can users trade on Deks?',
              answer:
                'Yes. Deks lets users trade any token, even if it’s not listed on a centralized exchange.',
            },
            {
              question: 'What are physical gift cards?',
              answer:
                'Physical gift cards are a tangible way to gift crypto. Customize them with your own designs and branding, and hand them out in the real world.',
            },
            {
              question: 'What is boosted listing?',
              answer:
                'Boosted listing puts your token front and center in Deks, driving more visibility, user engagement, and potential value growth.',
            },
            {
              question: 'What are push notifications?',
              answer:
                'Push notifications keep Deks users engaged with timely updates—like price changes, token news, and partner promotions—prompting them to trade, buy, or sell.',
            },
            {
              question: 'How does revenue share work?',
              answer:
                'Partners with revenue share enabled earn a percentage of the revenue their tokens generate on Deks.',
            },
          ]}
        />
      </div>
    </Container>
  );
};

export default Pricing;
